import { api } from '@/lib/api-client'
import { isDemoIssue, isDemoSite, isHiddenEntity } from '@/shared/constants/demo-data'

import {
  mapEntities,
  mapIssue,
  mapMandals,
  mapSites,
  mapTrends,
  type RawGisEntitiesResponse,
  type RawGisIssuesResponse,
  type RawGisMandalsResponse,
  type RawGisSitesResponse,
  type RawGisTrendsResponse,
} from './map-gis'
import { mapIssueDetail, type RawGisIssueDetailResponse } from './map-issue-detail'

import type {
  GisEntity,
  GisIssueDetail,
  GisIssuesPage,
  GisMandalsData,
  GisScope,
  GisSite,
  GisTrends,
} from './types'

/** Fiscal-year-to-date range (FY runs Apr 1 → Mar 31). Used to express `fy` as a `custom` range. */
function fyToDate(): { date_from: string; date_to: string } {
  const now = new Date()
  const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1
  return { date_from: `${startYear}-04-01`, date_to: now.toISOString().slice(0, 10) }
}

/**
 * Period params shared by the `/dashboard/gis/*` reads (`/mandals`, `/sites`, `/trends`), which accept
 * `7d|30d|90d|custom` but NOT `fy` — so `fy` becomes a fiscal-year-to-date `custom` range. Includes
 * `entity_id` + (for `custom`) `date_from`/`date_to`. The backend rejects `custom` without both dates.
 */
function periodScopeParams(scope: GisScope): Record<string, string> {
  const period =
    scope.period === 'fy'
      ? { period: 'custom', ...fyToDate() }
      : {
          period: scope.period,
          ...(scope.dateFrom && scope.dateTo
            ? { date_from: scope.dateFrom, date_to: scope.dateTo }
            : {}),
        }
  return { ...period, ...(scope.entityId ? { entity_id: scope.entityId } : {}) }
}

/**
 * `GET /dashboard/gis/mandals` — bulk per-mandal metrics + district rollup.
 * The api client unwraps the `{ status, data, message }` envelope, so this receives `data`.
 */
export async function getGisMandals(scope: GisScope): Promise<GisMandalsData> {
  const raw = await api.get<RawGisMandalsResponse>('/dashboard/gis/mandals', {
    params: periodScopeParams(scope),
  })
  return mapMandals(raw)
}

/**
 * `GET /dashboard/gis/sites` — lightweight markers for the map. Now windowed by the panel scope
 * (date-filter handoff §6): `survey_status`/`completion_pct`/`last_survey_date` reflect the selected
 * period, so markers move with the date filter like the choropleth does. Uses `mandalsScopeParams`
 * (same `7d|30d|90d|custom` vocabulary; `fy`→fiscal-year-to-date custom range), plus `entity_id`.
 * The `mandal`/`status` filters exist server-side but the UI searches/clusters client-side, so we
 * fetch the (entity's) full set once for the window and filter in the browser.
 */
export async function getGisSites(scope: GisScope): Promise<GisSite[]> {
  const raw = await api.get<RawGisSitesResponse>('/dashboard/gis/sites', {
    params: periodScopeParams(scope),
  })
  return mapSites(raw)
}

/** `GET /entities` — populates the entity filter (id + name + light context). */
export async function getGisEntities(): Promise<GisEntity[]> {
  const raw = await api.get<RawGisEntitiesResponse>('/entities')
  return mapEntities(raw)
}

const todayIso = (): string => new Date().toISOString().slice(0, 10)
const PERIOD_DAYS: Record<'7d' | '30d' | '90d', number> = { '7d': 7, '30d': 30, '90d': 90 }

/** ISO date `n` days before today (local clock → UTC date string, matching the other GIS ranges). */
function daysAgoIso(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return d.toISOString().slice(0, 10)
}

/**
 * `/issues` has no `period` keyword (unlike trends), so translate the GIS period to a concrete
 * `date_from`/`date_to` range: presets → `today − N days`; `fy` → fiscal-year-to-date; `custom` →
 * the supplied dates (omitted if incomplete). Mirrors `mandalsScopeParams`' fy→range handling.
 */
function issuesDateRange(scope: GisScope): Record<string, string> {
  if (scope.period === 'fy') {
    return fyToDate()
  }
  if (scope.period === 'custom') {
    return scope.dateFrom && scope.dateTo
      ? { date_from: scope.dateFrom, date_to: scope.dateTo }
      : {}
  }
  return { date_from: daysAgoIso(PERIOD_DAYS[scope.period]), date_to: todayIso() }
}

function issuesScopeParams(scope: GisScope): Record<string, string> {
  return { ...issuesDateRange(scope), ...(scope.entityId ? { entity_id: scope.entityId } : {}) }
}

/** One issues page = `per_page` cap. The backend honors this and returns `pagination.total_count`. */
const ISSUES_PER_PAGE = 200

/**
 * `GET /issues` — district-wide issue list for the GIS Issues tab (B1) + breakdown (B4). No `status`
 * param is sent so the response carries every status in the window; "active vs closed" is resolved
 * client-side (`lib/issues.ts`) so the list's status filter works without a refetch.
 *
 * Fetches one page of `per_page: 200` (the backend honors it, capped at 200) and returns the server's
 * `pagination.total_count` + `has_next` alongside the rows, so the board captions "first 200 of N"
 * accurately instead of guessing from the row count. Demo/hidden rows are filtered as in the source feed.
 */
async function fetchGisIssues(scope: GisScope): Promise<GisIssuesPage> {
  const raw = await api.get<RawGisIssuesResponse>('/issues', {
    params: { ...issuesScopeParams(scope), per_page: ISSUES_PER_PAGE },
  })
  const list = Array.isArray(raw) ? raw : (raw.issues ?? [])
  const pagination = Array.isArray(raw) ? undefined : (raw.pagination ?? undefined)
  const issues = list
    .filter((i) => !isDemoIssue(i.id) && !isDemoSite(i.site_id) && !isHiddenEntity(i.entity_id))
    .map(mapIssue)
  return {
    issues,
    totalCount: pagination?.total_count ?? issues.length,
    hasMore: pagination?.has_next ?? false,
  }
}

export async function getGisIssues(scope: GisScope): Promise<GisIssuesPage> {
  return fetchGisIssues(scope)
}

/**
 * `GET /dashboard/gis/trends` — adaptive-bucket time series for the lens sparklines.
 * Omitting `mandal` returns district-wide series; passing it (P2) scopes the same shape to one
 * mandal. The backend resolves `mandal` as either a `mandal_id` (P0) or the raw name string.
 *
 * Shares `periodScopeParams` with the other `/dashboard/gis/*` reads so the `fy` preset is sent as a
 * fiscal-year-to-date `custom` range (the endpoint accepts `7d|30d|90d|custom`, not `fy`).
 */
export async function getGisTrends({
  mandal,
  ...scope
}: GisScope & { mandal?: string }): Promise<GisTrends> {
  const raw = await api.get<RawGisTrendsResponse>('/dashboard/gis/trends', {
    params: { ...periodScopeParams(scope), ...(mandal ? { mandal } : {}) },
    // Mandal-scoped trends have an inline TrendsUnavailable fallback, so don't fire the global toast.
    ...(mandal ? { suppressErrorToast: true } : {}),
  })
  return mapTrends(raw)
}

/**
 * `GET /issues/{id}` — fully-hydrated issue + event timeline for the in-panel drill-down.
 * Suppresses the global error toast; the drawer surfaces failures (incl. 404 → not found / not
 * accessible) inline.
 */
export async function getGisIssueDetail(issueId: string): Promise<GisIssueDetail> {
  const raw = await api.get<RawGisIssueDetailResponse>(`/issues/${issueId}`, {
    suppressErrorToast: true,
  })
  return mapIssueDetail(raw)
}
